import React, { useState, useMemo } from "react";
import { SelectedStudentCountCard } from "@/components/SelectedStudentCountCard";
import { FilteredStudentsList } from "@/components/FilteredStudentsList";
import { FunnelChartState } from "@/components/Funnel/types";
import { PaginatedResults } from "@/util/pagination";
import { Student } from "@/types/Student";
import { useTranslation } from "react-i18next";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { FilterContext } from "@/types/FilterContext";
import { StudentOrdering } from "@/types/StudentOrdering";
import { useUrlState } from "@/hooks/useUrlSerializer";
import { CommunityStatusSelectOption } from "@/components/FunnelsChart";
import { FunnelStateMaps } from "@/util/funnelGroups";
import { YesNoEnum } from "@/__generated__/types";

export const FunnelStudents = ({
  selectedFunnelState,
  onClearSelection,
  filterState,
  communityStatus,
  date,
}: {
  selectedFunnelState: FunnelChartState | null;
  onClearSelection: () => void;
  filterState: FilterState;
  communityStatus: CommunityStatusSelectOption | null;
  date: Date;
}) => {
  const { t } = useTranslation();
  const [studentsLoading, setStudentsLoading] = useState(false);
  const handleResultsUpdate = (results: PaginatedResults<Student>) => {
    setStudentsLoading(results.loading);
  };
  const urlOrdering = useUrlState("STUDENT_ORDERING");
  const [studentOrdering, setStudentOrdering] = useState<
    StudentOrdering | undefined
  >(
    new StudentOrdering(
      urlOrdering?.field || null,
      urlOrdering?.direction || null,
      "funnelChart",
    ),
  );

  // Add community status to student filterState
  const studentFilterState = useMemo(() => {
    return filterState?.clone({
      data: {
        inCommunity:
          communityStatus === "notInCommunity" ? YesNoEnum.No : YesNoEnum.Yes,
      },
    });
  }, [communityStatus, filterState]);

  // add funnel specific student context data
  const filterContext = useMemo(() => {
    const backendFunnelStates = selectedFunnelState
      ? FunnelStateMaps["FUNNEL"][selectedFunnelState]
      : [];

    return new FilterContext("funnelChart", {
      funnelChartDate: date,
      funnelChartFunnelStates: backendFunnelStates,
    });
  }, [date, selectedFunnelState]);

  return (
    <>
      {selectedFunnelState && (
        <SelectedStudentCountCard
          description={t(
            "selectedStudentCountCard.funnelState." + selectedFunnelState,
          )}
          onClear={onClearSelection}
          loading={studentsLoading}
        ></SelectedStudentCountCard>
      )}
      <FilteredStudentsList
        filterState={studentFilterState}
        context={filterContext}
        ordering={studentOrdering}
        description={t("zeemeeFunnel.studentsDescription")}
        setOrdering={setStudentOrdering}
        title={t("zeemeeFunnel.funnelState")}
        onQueryResults={handleResultsUpdate}
        profileSource="Funnel"
      />
    </>
  );
};
